import type { FleetInvitationResponse } from '@emma/api_client/types';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  IconButton,
  Link,
  Menu,
  Table,
  Tag,
  Text,
  Tooltip,
  useToast,
} from '@lego/klik-ui';
import { MinusBold, PlusBold } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { Navigate, Link as ReactRouterLink, useParams } from 'react-router-dom';
import { BreadCrumb, Loader } from '@/components/common';
import { TextHeader } from '@/components/common/headers/TextHeader';
import { useFetch } from '@/hooks/api';
import { useDocumentTitle } from '@/hooks/common';
import { type DeviceResponse, useDevice } from '@/hooks/device';
import { type FleetResponse, useFleets } from '@/hooks/fleet';
import {
  type FleetBatchAssignmentResponse,
  useAssignDevicesToFleet,
  useUnassignDevicesFromFleet,
} from '@/hooks/fleet/fleet_batch_assignment_resource';
import { useFleetInvitationsByInvitedWorkspace } from '@/hooks/fleet/workspace_fleet_invitations_resource';
import type {
  WorkspaceListResponse,
  WorkspaceResponse,
} from '@/hooks/workspace/workspaces_resource';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';

const DeviceActionsFleetAssignmentPageContent = ({ deviceId }: { deviceId: number }) => {
  const { data: device, error, isLoading, mutate } = useDevice(deviceId);
  useDocumentTitle('Fleet Assignment');

  if (isLoading || error || !device) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  return (
    <>
      <BreadCrumb
        crumbs={[
          { label: 'Workspaces', path: paths.workspaces },
          {
            label: `Workspace #${device.workspaceId}`,
            path: paths.workspaceById(device.workspaceId),
          },
          {
            label: `Device #${device.id}`,
            path: paths.deviceById(device.id),
          },
          { label: 'Fleet Management' },
        ]}
      />
      <DeviceFleetManagement device={device} onChange={mutate} />
    </>
  );
};

export const DeviceActionsFleetAssignmentPage = () => {
  const { deviceId } = useParams();
  if (!deviceId || !Number.parseInt(deviceId, 10)) {
    return <Navigate to={paths.devices} />;
  }

  return <DeviceActionsFleetAssignmentPageContent deviceId={Number.parseInt(deviceId, 10)} />;
};

type OwnedFleetInfo = FleetResponse & {
  isInvited: false;
};

type InvitedFleetInfo = {
  id: number;
  workspaceId: number;
  ownerWorkspaceName: string;
  isInvited: true;
};

type FleetInfo = OwnedFleetInfo | InvitedFleetInfo;

const buildWorkspaceNameMap = (workspaces: WorkspaceResponse[]): Map<number, string> => {
  const map = new Map<number, string>();
  for (const workspace of workspaces) {
    map.set(workspace.id, workspace.name);
  }
  return map;
};

const createInvitedFleetInfo = (
  fleetId: number,
  ownerWorkspaceId: number,
  workspaceNameMap: Map<number, string>
): InvitedFleetInfo => {
  const ownerWorkspaceName =
    workspaceNameMap.get(ownerWorkspaceId) ?? `Workspace #${ownerWorkspaceId}`;

  return {
    id: fleetId,
    workspaceId: ownerWorkspaceId,
    ownerWorkspaceName,
    isInvited: true,
  };
};

const categorizeDeviceFleets = (
  deviceFleetIds: number[],
  workspaceFleets: FleetResponse[],
  fleetInvitations: FleetInvitationResponse[],
  workspaceNameMap: Map<number, string>
): { owned: OwnedFleetInfo[]; invited: InvitedFleetInfo[] } => {
  const ownedFleetIds = new Set(workspaceFleets.map((f) => f.id));
  const owned: OwnedFleetInfo[] = [];
  const invited: InvitedFleetInfo[] = [];

  for (const fleetId of deviceFleetIds) {
    if (ownedFleetIds.has(fleetId)) {
      const fleet = workspaceFleets.find((f) => f.id === fleetId);
      if (fleet) {
        owned.push({ ...fleet, isInvited: false });
      }
    } else {
      const invitation = fleetInvitations.find((i) => i.fleetId === fleetId);
      if (invitation) {
        invited.push(createInvitedFleetInfo(fleetId, invitation.workspaceId, workspaceNameMap));
      } else {
        invited.push(createInvitedFleetInfo(fleetId, 0, workspaceNameMap));
      }
    }
  }

  return { owned, invited };
};

const getAvailableFleets = (
  deviceFleetIds: number[],
  workspaceFleets: FleetResponse[],
  fleetInvitations: FleetInvitationResponse[],
  workspaceNameMap: Map<number, string>
): { owned: OwnedFleetInfo[]; invited: InvitedFleetInfo[] } => {
  const owned = workspaceFleets
    .filter((fleet) => !deviceFleetIds.includes(fleet.id))
    .map((fleet): OwnedFleetInfo => ({ ...fleet, isInvited: false }));

  const invited = fleetInvitations
    .filter((invitation) => !deviceFleetIds.includes(invitation.fleetId))
    .map((invitation) =>
      createInvitedFleetInfo(invitation.fleetId, invitation.workspaceId, workspaceNameMap)
    );

  return { owned, invited };
};

export const DeviceFleetManagement = ({
  device,
  onChange,
}: {
  device: DeviceResponse;
  onChange: () => void;
}) => {
  const [removingFleetId, setRemovingFleetId] = useState<number | null>(null);
  const [addingFleetId, setAddingFleetId] = useState<number | null>(null);
  const toast = useToast();

  const { data: workspaceFleetsData, isLoading: isLoadingWorkspaceFleets } = useFleets(
    device.workspaceId
  );

  const { data: fleetInvitationsData, isLoading: isLoadingInvitations } =
    useFleetInvitationsByInvitedWorkspace(device.workspaceId);

  const ownerWorkspaceIds = fleetInvitationsData
    ? [...new Set(fleetInvitationsData.invitations.map((i) => i.workspaceId))]
    : [];

  const { data: ownerWorkspacesData, isLoading: isLoadingOwnerWorkspaces } =
    useFetch<WorkspaceListResponse>(
      fleetInvitationsData && fleetInvitationsData.invitations.length > 0
        ? `workspaces/owners/${ownerWorkspaceIds.join(',')}`
        : null,
      (client) =>
        client.v2.workspaces.$get({
          query: { idIn: ownerWorkspaceIds, limit: '1000' },
        })
    );

  const removeFromFleet = useUnassignDevicesFromFleet();
  const addToFleet = useAssignDevicesToFleet();

  const handleBatchResponse = (response: FleetBatchAssignmentResponse) => {
    if (response.failed.length > 0) {
      for (const failure of response.failed) {
        toast(ToastOptions.error(`Failed to update device #${failure.deviceId}`, failure.error));
      }
    }
    return response.succeeded.length > 0;
  };

  const handleRemoveFromFleet = async (fleet: FleetInfo) => {
    setRemovingFleetId(fleet.id);
    try {
      const response = await removeFromFleet({
        deviceIds: [device.id],
        fleetId: fleet.id,
        workspaceId: fleet.workspaceId,
      });
      if (handleBatchResponse(response)) {
        onChange();
      }
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setRemovingFleetId(null);
    }
  };

  const handleAddToFleet = async (fleet: FleetInfo) => {
    setAddingFleetId(fleet.id);
    try {
      const response = await addToFleet({
        deviceIds: [device.id],
        fleetId: fleet.id,
        workspaceId: fleet.workspaceId,
      });
      if (handleBatchResponse(response)) {
        onChange();
      }
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setAddingFleetId(null);
    }
  };

  const isLoading = isLoadingWorkspaceFleets || isLoadingInvitations || isLoadingOwnerWorkspaces;

  if (isLoading) {
    return <Loader />;
  }

  const workspaceFleets = workspaceFleetsData?.fleets ?? [];
  const fleetInvitations = fleetInvitationsData?.invitations ?? [];
  const ownerWorkspaces = ownerWorkspacesData?.workspaces ?? [];

  const workspaceNameMap = buildWorkspaceNameMap(ownerWorkspaces);

  const currentFleets = categorizeDeviceFleets(
    device.fleets.map((fleet) => fleet.id),
    workspaceFleets,
    fleetInvitations,
    workspaceNameMap
  );

  const availableFleets = getAvailableFleets(
    device.fleets.map((fleet) => fleet.id),
    workspaceFleets,
    fleetInvitations,
    workspaceNameMap
  );

  const allCurrentFleets: FleetInfo[] = [...currentFleets.owned, ...currentFleets.invited];
  const allAvailableFleets: FleetInfo[] = [...availableFleets.owned, ...availableFleets.invited];
  const hasAvailableFleets = allAvailableFleets.length > 0;

  return (
    <Box bg="white" boxShadow="md" mx={4} my={4} p={6} rounded="md">
      <Flex alignItems="center" justifyContent="space-between" mb={4}>
        <TextHeader tagLevel="h3" title="Fleet Memberships" />
        <Menu>
          <Tooltip
            isDisabled={hasAvailableFleets}
            label="No fleets available to add"
            placement="left"
          >
            <Box>
              <Menu.Button
                as={Button}
                isDisabled={!hasAvailableFleets}
                leftIcon={<Icon as={PlusBold} />}
                size="sm"
                variant="primary"
              >
                Add to Fleet
              </Menu.Button>
            </Box>
          </Tooltip>
          <Menu.List maxH="300px" overflowY="auto">
            {availableFleets.owned.length > 0 && (
              <>
                <Text color="gray.500" fontSize="xs" fontWeight="medium" px={3} py={1}>
                  YOUR FLEETS
                </Text>
                {availableFleets.owned.map((fleet) => (
                  <Menu.Item
                    isDisabled={addingFleetId === fleet.id}
                    key={fleet.id}
                    onClick={() => handleAddToFleet(fleet)}
                  >
                    <Flex alignItems="center" gap={2}>
                      <Text>
                        {fleet.name} (id: {fleet.id})
                      </Text>
                      {fleet.description && (
                        <Text color="gray.400" fontSize="sm">
                          - {fleet.description}
                        </Text>
                      )}
                    </Flex>
                  </Menu.Item>
                ))}
              </>
            )}
            {availableFleets.owned.length > 0 && availableFleets.invited.length > 0 && (
              <Divider my={1} />
            )}
            {availableFleets.invited.length > 0 && (
              <>
                <Text color="gray.500" fontSize="xs" fontWeight="medium" px={3} py={1}>
                  INVITED FLEETS
                </Text>
                {availableFleets.invited.map((fleet) => (
                  <Menu.Item
                    isDisabled={addingFleetId === fleet.id}
                    key={fleet.id}
                    onClick={() => handleAddToFleet(fleet)}
                  >
                    <Flex alignItems="center" gap={2}>
                      <Text>
                        {fleet.ownerWorkspaceName} / Fleet #{fleet.id}
                      </Text>
                    </Flex>
                  </Menu.Item>
                ))}
              </>
            )}
          </Menu.List>
        </Menu>
      </Flex>

      {allCurrentFleets.length === 0 ? (
        <Box bg="gray.50" borderRadius="md" p={8} textAlign="center">
          <Text color="gray.500">This device is not assigned to any fleets.</Text>
          <Text color="gray.400" fontSize="sm" mt={2}>
            Click "Add to Fleet" to assign this device to a fleet.
          </Text>
        </Box>
      ) : (
        <Table variant="simple">
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader>Fleet</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader width="100px" />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {allCurrentFleets.map((fleet) => (
              <Table.Row key={fleet.id}>
                <Table.Cell>
                  <Flex alignItems="center" gap={2}>
                    {fleet.isInvited ? (
                      <>
                        {fleet.workspaceId > 0 ? (
                          <Link
                            as={ReactRouterLink}
                            p={0}
                            to={paths.workspaceById(fleet.workspaceId)}
                          >
                            {fleet.ownerWorkspaceName}
                          </Link>
                        ) : (
                          <Text fontWeight="medium">{fleet.ownerWorkspaceName}</Text>
                        )}
                        <Text>/</Text>
                        {fleet.workspaceId > 0 ? (
                          <Link
                            as={ReactRouterLink}
                            fontWeight="medium"
                            p={0}
                            to={paths.fleetById(fleet.workspaceId, fleet.id)}
                          >
                            Fleet #{fleet.id}
                          </Link>
                        ) : (
                          <Text fontWeight="medium">Fleet #{fleet.id}</Text>
                        )}
                      </>
                    ) : (
                      <Link
                        as={ReactRouterLink}
                        fontWeight="medium"
                        p={0}
                        to={paths.fleetById(fleet.workspaceId, fleet.id)}
                      >
                        {fleet.name}
                      </Link>
                    )}
                    {!fleet.isInvited && fleet.description && (
                      <Text color="gray.400" fontSize="sm">
                        - {fleet.description}
                      </Text>
                    )}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  {fleet.isInvited ? (
                    <Tag colorScheme="light-blue" size="sm">
                      Invited
                    </Tag>
                  ) : (
                    <Tag colorScheme="gray" size="sm">
                      Owned
                    </Tag>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Tooltip label="Remove from fleet">
                    <IconButton
                      aria-label="Remove from fleet"
                      icon={<Icon as={MinusBold} />}
                      isLoading={removingFleetId === fleet.id}
                      onClick={() => handleRemoveFromFleet(fleet)}
                      size="sm"
                      variant="ghost"
                    />
                  </Tooltip>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {allCurrentFleets.length > 0 && (
        <Text color="gray.400" fontSize="sm" mt={4}>
          Note: Removing a device from a fleet will stop any active deployments from that fleet.
        </Text>
      )}
    </Box>
  );
};
